import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Layout as AntLayout, Menu, Avatar, Dropdown, Button, Drawer } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HomeOutlined,
  FileTextOutlined,
  SettingOutlined,
  CarOutlined,
  LogoutOutlined,
  UserOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  VideoCameraOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { Car, House, Menu as MenuIcon, Settings, X } from 'lucide';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';
import MorphingIcon from './MorphingIcon';
import { TESTS_WORKSPACE_ENABLED } from '../config/features';
import { usePlatformGuide } from './PlatformGuide';
import './Layout.css';

const { Header, Content, Footer } = AntLayout;
const MOBILE_BREAKPOINT = 992;
const COMPACT_NAV_BREAKPOINT = 1800;
const NAV_MORPH_ICONS = {
  '/': House,
  '/admin/auto-brand': Car,
  '/admin-group': Settings,
} as const;

/**
 * Какой пункт меню подсветить для текущего пути.
 *
 * Раньше это была вложенная цепочка условий, и любой новый раздел вида
 * /admin/<что-то> проваливался в общее '/admin': подсвечивался не тот пункт,
 * а декоративный маркер уезжал к чужой иконке. Теперь — таблица префиксов,
 * самый длинный выигрывает, добавление раздела ничего не ломает.
 */
const NAV_PREFIXES: Array<[string, string]> = [
  ['/admin/auto-brand', '/admin/auto-brand'],
  ['/admin', '/admin-group'],
  ['/videos', '/'],
  ['/tests', '/'],
  ['/results', '/'],
];

function resolveNavKey(pathname: string): string {
  const hit = NAV_PREFIXES
    .filter(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`))
    .sort((a, b) => b[0].length - a[0].length)[0];
  return hit ? hit[1] : pathname;
}
let lastDesktopNavKey: string | null = null;
let lastDesktopNavPosition: { x: number; y: number } | null = null;
let cachedAutoBrandAccess: { email: string; allowed: boolean } | null = null;

interface LayoutProps {
  children: React.ReactNode;
  wide?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, wide = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const { openGuide, unseenCount } = usePlatformGuide();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
  const [isCompactNav, setIsCompactNav] = useState(
    typeof window !== 'undefined' && window.innerWidth <= COMPACT_NAV_BREAKPOINT
  );
  const [canManageAutoBrandTests, setCanManageAutoBrandTests] = useState(
    () => Boolean(user?.email && cachedAutoBrandAccess?.email === user.email && cachedAutoBrandAccess.allowed)
  );
  const navContainerRef = useRef<HTMLDivElement>(null);
  const navBeadRef = useRef<HTMLSpanElement>(null);
  const navBeadHaloRef = useRef<HTMLSpanElement>(null);
  const navBeadFrameRef = useRef<number | null>(null);
  const navBeadMotionRef = useRef({
    initialized: lastDesktopNavPosition != null,
    x: lastDesktopNavPosition?.x || 0,
    y: lastDesktopNavPosition?.y || 0,
    targetX: 0,
    targetY: 0,
    velocityX: 0,
    velocityY: 0,
    lastTime: 0,
  });

  useEffect(() => {
    const mobileMql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const compactNavMql = window.matchMedia(`(max-width: ${COMPACT_NAV_BREAKPOINT}px)`);
    const handler = () => {
      setIsMobile(mobileMql.matches);
      setIsCompactNav(compactNavMql.matches);
      if (!compactNavMql.matches) setMobileMenuOpen(false);
    };
    handler();
    mobileMql.addEventListener('change', handler);
    compactNavMql.addEventListener('change', handler);
    return () => {
      mobileMql.removeEventListener('change', handler);
      compactNavMql.removeEventListener('change', handler);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    if (!user?.email) {
      cachedAutoBrandAccess = null;
      setCanManageAutoBrandTests(false);
      return () => {
        isMounted = false;
      };
    }
    if (cachedAutoBrandAccess?.email === user.email) {
      setCanManageAutoBrandTests(cachedAutoBrandAccess.allowed);
      return () => {
        isMounted = false;
      };
    }
    api
      .get('/admin/auto-brand/access')
      .then((response) => {
        if (isMounted) {
          const allowed = Boolean(response?.data?.can_access);
          cachedAutoBrandAccess = { email: user.email, allowed };
          setCanManageAutoBrandTests(allowed);
        }
      })
      .catch(() => {
        if (isMounted) {
          cachedAutoBrandAccess = { email: user.email, allowed: false };
          setCanManageAutoBrandTests(false);
        }
      });
    return () => {
      isMounted = false;
    };
  }, [user?.email, user?.role]);

  const menuItems = [
    {
      key: '/',
      icon: <HomeOutlined />,
      label: 'Личный кабинет',
    },
    ...(canManageAutoBrandTests
      ? [
          {
            key: '/admin/auto-brand',
            icon: <CarOutlined />,
            label: 'Авто-тесты',
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: '/admin-group',
            icon: <SettingOutlined />,
            label: 'Администрирование',
            children: [
              ...(TESTS_WORKSPACE_ENABLED
                ? [{ key: '/admin/tests', icon: <FileTextOutlined />, label: 'Тесты' }]
                : []),
              {
                key: '/admin/board',
                icon: <UsergroupAddOutlined />,
                label: 'Назначения',
              },
              {
                key: '/admin/questions',
                icon: <FileTextOutlined />,
                label: 'Банк вопросов',
              },
              {
                key: '/admin',
                icon: <SettingOutlined />,
                label: t('nav.admin'),
              },
              {
                key: '/admin/videos',
                icon: <VideoCameraOutlined />,
                label: 'Видео: управление',
              },
            ],
          },
        ]
      : []),
  ];

  const userMenuItems = [
    {
      key: 'user',
      label: (
        <div style={{ padding: '4px 0' }}>
          <div style={{ fontWeight: 500 }}>{user?.full_name}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{user?.email}</div>
        </div>
      ),
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('nav.logout'),
      onClick: () => {
        logout(); // редирект на центральный logout
      },
    },
  ];

  const onMenuClick = (key: string) => {
    navigate(key);
    setMobileMenuOpen(false);
  };

  const selectedMenuKey = resolveNavKey(location.pathname);
  const initialMorphKey = lastDesktopNavKey && lastDesktopNavKey in NAV_MORPH_ICONS
    ? lastDesktopNavKey as keyof typeof NAV_MORPH_ICONS
    : selectedMenuKey as keyof typeof NAV_MORPH_ICONS;
  const [navMorphKey, setNavMorphKey] = useState<keyof typeof NAV_MORPH_ICONS>(initialMorphKey);
  const navMorphIcon = useMemo(
    () => NAV_MORPH_ICONS[navMorphKey] || Settings,
    [navMorphKey]
  );

  useEffect(() => {
    const nextKey = selectedMenuKey in NAV_MORPH_ICONS
      ? selectedMenuKey as keyof typeof NAV_MORPH_ICONS
      : '/admin-group';
    const frame = window.requestAnimationFrame(() => setNavMorphKey(nextKey));
    return () => window.cancelAnimationFrame(frame);
  }, [selectedMenuKey]);

  const syncNavIconWithBead = useCallback(() => {
    const container = navContainerRef.current;
    const bead = navBeadRef.current;
    if (!container || !bead) return;

    const containerRect = container.getBoundingClientRect();
    const beadSize = bead.offsetWidth || 34;
    const beadCenter = navBeadMotionRef.current.x + beadSize / 2;
    let closestItem: HTMLElement | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    container.querySelectorAll<HTMLElement>('.ant-menu-item').forEach((item) => {
      const icon = item.querySelector<HTMLElement>('.anticon');
      if (!icon) return;
      const rect = icon.getBoundingClientRect();
      const iconCenter = rect.left - containerRect.left + rect.width / 2;
      const distance = Math.abs(iconCenter - beadCenter);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    container.querySelectorAll<HTMLElement>('.ant-menu-item').forEach((item) => {
      item.classList.toggle(
        'bead-icon-active',
        item === closestItem && closestDistance <= beadSize * 0.62
      );
    });
  }, []);

  const moveNavBead = useCallback((key: string, instant = false): boolean => {
    const container = navContainerRef.current;
    const bead = navBeadRef.current;
    const halo = navBeadHaloRef.current;
    const menuItem = Array.from(container?.querySelectorAll<HTMLElement>('.ant-menu-item') || [])
      .find((item) => item.getAttribute('data-menu-id')?.endsWith(key));
    const targetIcon = menuItem?.querySelector<HTMLElement>('.anticon');
    if (!container || !bead || !halo || !targetIcon) return false;

    const containerRect = container.getBoundingClientRect();
    const iconRect = targetIcon.getBoundingClientRect();
    const beadSize = bead.offsetWidth || 34;
    const motion = navBeadMotionRef.current;
    motion.targetX = iconRect.left - containerRect.left + (iconRect.width - beadSize) / 2;
    motion.targetY = iconRect.top - containerRect.top + (iconRect.height - beadSize) / 2;

    if (navBeadFrameRef.current != null) {
      window.cancelAnimationFrame(navBeadFrameRef.current);
      navBeadFrameRef.current = null;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const applyTransform = (stretch = 0) => {
      lastDesktopNavPosition = { x: motion.x, y: motion.y };
      bead.style.transform = `translate3d(${motion.x}px, ${motion.y}px, 0) scale(${1 + stretch}, ${1 - stretch * 0.42})`;
      halo.style.transform = `translate3d(${motion.x}px, ${motion.y}px, 0)`;
      bead.style.opacity = '1';
      halo.style.opacity = '';
      syncNavIconWithBead();
    };

    if (!motion.initialized || instant || reduceMotion) {
      motion.initialized = true;
      motion.x = motion.targetX;
      motion.y = motion.targetY;
      motion.velocityX = 0;
      motion.velocityY = 0;
      motion.lastTime = 0;
      bead.style.transformOrigin = '50% 50%';
      applyTransform();
      return true;
    }

    motion.lastTime = 0;
    const tick = (now: number) => {
      const deltaSeconds = motion.lastTime
        ? Math.min(0.032, Math.max(0.008, (now - motion.lastTime) / 1000))
        : 1 / 60;
      motion.lastTime = now;
      const displacementX = motion.x - motion.targetX;
      const displacementY = motion.y - motion.targetY;
      motion.velocityX += (-190 * displacementX - 26 * motion.velocityX) * deltaSeconds;
      motion.velocityY += (-190 * displacementY - 26 * motion.velocityY) * deltaSeconds;
      motion.x += motion.velocityX * deltaSeconds;
      motion.y += motion.velocityY * deltaSeconds;

      const stretch = Math.min(0.22, Math.abs(motion.velocityX) * 0.0014);
      bead.style.transformOrigin = motion.velocityX >= 0 ? '12% 50%' : '88% 50%';
      applyTransform(stretch);

      const settled = Math.abs(displacementX) < 0.12
        && Math.abs(displacementY) < 0.12
        && Math.abs(motion.velocityX) < 8
        && Math.abs(motion.velocityY) < 8;
      if (settled) {
        motion.x = motion.targetX;
        motion.y = motion.targetY;
        motion.velocityX = 0;
        motion.velocityY = 0;
        bead.style.transformOrigin = '50% 50%';
        applyTransform();
        navBeadFrameRef.current = null;
        return;
      }
      navBeadFrameRef.current = window.requestAnimationFrame(tick);
    };
    navBeadFrameRef.current = window.requestAnimationFrame(tick);
    return true;
  }, [syncNavIconWithBead]);

  useLayoutEffect(() => {
    if (isCompactNav) {
      navBeadMotionRef.current.initialized = false;
      if (navBeadFrameRef.current != null) window.cancelAnimationFrame(navBeadFrameRef.current);
      navBeadFrameRef.current = null;
      return;
    }
    const previousKey = lastDesktopNavKey;
    let retryFrame: number | null = null;
    let retries = 0;
    const positionBead = () => {
      if (!navBeadMotionRef.current.initialized && previousKey && previousKey !== selectedMenuKey) {
        const previousPlaced = moveNavBead(previousKey, true);
        if (!previousPlaced && retries < 12) {
          retries += 1;
          retryFrame = window.requestAnimationFrame(positionBead);
          return;
        }
      }
      const currentPlaced = moveNavBead(selectedMenuKey);
      if (!currentPlaced && retries < 12) {
        retries += 1;
        retryFrame = window.requestAnimationFrame(positionBead);
        return;
      }
      if (currentPlaced) lastDesktopNavKey = selectedMenuKey;
    };
    const frame = window.requestAnimationFrame(positionBead);
    return () => {
      window.cancelAnimationFrame(frame);
      if (retryFrame != null) window.cancelAnimationFrame(retryFrame);
    };
  }, [canManageAutoBrandTests, isCompactNav, language, moveNavBead, selectedMenuKey]);

  useEffect(() => {
    if (isCompactNav || !navContainerRef.current) return;
    const container = navContainerRef.current;
    let previousWidth = container.getBoundingClientRect().width;
    const observer = new ResizeObserver(() => {
      const nextWidth = container.getBoundingClientRect().width;
      if (Math.abs(nextWidth - previousWidth) < 0.5) return;
      previousWidth = nextWidth;
      moveNavBead(lastDesktopNavKey || selectedMenuKey, true);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [isCompactNav, moveNavBead, selectedMenuKey]);

  useEffect(() => {
    if (isCompactNav || !navContainerRef.current) return;
    const container = navContainerRef.current;
    let syncFrame: number | null = null;
    const observer = new MutationObserver(() => {
      if (syncFrame != null) window.cancelAnimationFrame(syncFrame);
      syncFrame = window.requestAnimationFrame(() => {
        syncFrame = null;
        syncNavIconWithBead();
      });
    });
    observer.observe(container, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => {
      observer.disconnect();
      if (syncFrame != null) window.cancelAnimationFrame(syncFrame);
    };
  }, [isCompactNav, syncNavIconWithBead]);

  useEffect(() => () => {
    if (navBeadFrameRef.current != null) window.cancelAnimationFrame(navBeadFrameRef.current);
  }, []);

  return (
    <AntLayout className="app-layout">
      <Header className="app-header">
        <div className="header-content">
          <a
            className="logo"
            href="/"
            aria-label={t('nav.portal')}
            title={t('nav.portal')}
          >
            <img 
              src="/images/febest-logo.svg" 
              alt="FEBEST" 
              className="logo-image"
              style={{ height: '45px' }}
            />
            <span className="logo-subtitle">Testing</span>
          </a>
          {!isCompactNav && (
            <div ref={navContainerRef} className="header-bead-nav">
              <span
                ref={navBeadHaloRef}
                className="header-nav-bead-halo"
                aria-hidden="true"
                style={lastDesktopNavPosition
                  ? { transform: `translate3d(${lastDesktopNavPosition.x}px, ${lastDesktopNavPosition.y}px, 0)` }
                  : { opacity: 0 }}
              />
              <span
                ref={navBeadRef}
                className="header-nav-bead"
                aria-hidden="true"
                style={lastDesktopNavPosition
                  ? { transform: `translate3d(${lastDesktopNavPosition.x}px, ${lastDesktopNavPosition.y}px, 0)` }
                  : { opacity: 0 }}
              >
                <MorphingIcon
                  icon={navMorphIcon}
                  size={18}
                  strokeWidth={2}
                  className="header-nav-bead-icon"
                />
              </span>
              <Menu
                theme="light"
                mode="horizontal"
                selectedKeys={[selectedMenuKey]}
                items={menuItems}
                onClick={({ key }) => navigate(key)}
                style={{ width: '100%', minWidth: 0, justifyContent: 'center' }}
              />
            </div>
          )}
          <div className="header-actions">
            <Button type="text" icon={<QuestionCircleOutlined />} onClick={openGuide}
              data-guide="help" className="platform-guide-help" aria-label="Помощь по платформе">
              <span className="platform-guide-help-label">Помощь</span>
              {unseenCount > 0 && <span className="platform-guide-help-dot" aria-label="Есть новые инструкции" />}
            </Button>
            {/* Кнопка «На портал» отключена: отдельного портала сейчас нет. */}
            {isCompactNav && (
              <Button
                type="text"
                icon={<MorphingIcon icon={mobileMenuOpen ? X : MenuIcon} size={21} />}
                onClick={() => setMobileMenuOpen(true)}
                className="header-menu-trigger"
                aria-label="Меню"
              />
            )}
            <Button
              type="text"
              icon={<GlobalOutlined />}
              onClick={() => setLanguage(language === 'ru' ? 'en' : 'ru')}
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              {language === 'ru' ? 'EN' : 'RU'}
            </Button>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Button type="text" className="header-user-btn">
                <Avatar icon={<UserOutlined />} style={{ marginRight: isMobile ? 0 : 8 }} />
                {!isMobile && user?.full_name}
              </Button>
            </Dropdown>
          </div>
        </div>
      </Header>
      <Drawer
        title={t('nav.menu')}
        placement="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        className="app-drawer"
        styles={{ body: { padding: 0 } }}
      >
        <Menu
          mode="vertical"
          selectedKeys={[selectedMenuKey]}
          items={menuItems}
          onClick={({ key }) => onMenuClick(key)}
          style={{ borderRight: 0, padding: '8px 0' }}
        />
      </Drawer>
      <Content className={wide ? 'app-content app-content-wide' : 'app-content'}>
        {children}
      </Content>
      <Footer className="app-footer">
        {t('footer.text')} ©2026{t('footer.developedBy') ? ` · ${t('footer.developedBy')}` : ''}
      </Footer>
    </AntLayout>
  );
};

export default Layout;
